import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { alertasApi, type Alerta, type SeveridadeAlerta } from '@/lib/api/core';
import { useAuthStore } from '@/lib/stores/authStore';
import { useMockStore } from '@/lib/stores/mockStore';
import { Info } from '@/design-system/Info';

const CHIP: Record<SeveridadeAlerta, string> = {
  critica: 'crit',
  atencao: 'warn',
  oportunidade: 'good',
};

const ROTULO: Record<SeveridadeAlerta, string> = {
  critica: 'Crítico',
  atencao: 'Atenção',
  oportunidade: 'Oportunidade',
};

type Filtro = '' | 'nao-lidos' | SeveridadeAlerta;

export function AlertasPage() {
  const token = useAuthStore((s) => s.token) || '';
  const setBairroActiveId = useMockStore((s) => s.setBairroActiveId);
  const queryClient = useQueryClient();

  const [filtro, setFiltro] = useState<Filtro>('');

  const { data: alertas = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['alertas'],
    queryFn: () => alertasApi.list(token),
    enabled: !!token,
  });

  /**
   * Sem atualização otimista de propósito: "lido" é estado gravado no
   * servidor, e pintar o cartão antes da confirmação faria a tela afirmar uma
   * gravação que ainda pode falhar. A lista é reconsultada depois do PUT — a
   * mesma chave `['alertas']` que a Sidebar usa, então o contador do menu
   * acompanha sem precisar de um segundo contador.
   */
  const marcar = useMutation({
    mutationFn: ({ id, lida }: { id: string; lida: boolean }) =>
      alertasApi.marcarLido(id, lida, token),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alertas'] }),
  });

  const naoLidos = alertas.filter((a) => !a.lida).length;
  const contar = (s: SeveridadeAlerta) => alertas.filter((a) => a.severidade === s).length;

  const lista = alertas.filter((a) => {
    if (filtro === '') return true;
    if (filtro === 'nao-lidos') return !a.lida;
    return a.severidade === filtro;
  });

  const abrirRegistros = (a: Alerta) => {
    // A tela de Território foca pelo `bairroActiveId` do store, não por
    // parâmetro de URL. É uma convenção anterior ao padrão `?contato=<id>`
    // da task 5.2 e está registrada como dívida — inventar aqui uma terceira
    // convenção seria pior que reusar a que o destino de fato lê.
    if (a.referencia) setBairroActiveId(a.referencia);
  };

  return (
    <div className="page" id="p-alertas">
      <div className="head">
        <div>
          <h1 style={{ color: 'var(--eleg)' }}>Alertas estratégicos</h1>
          <p>
            {/*
              proto:1007 prometia fonte, período, link para os registros de
              origem e ação sugerida em CADA alerta. Regra e fonte saem nos
              cinco; o período é real em um só (o de SLA), o `link` leva à tela
              e não ao registro, e a ação sugerida existe em um único alerta.
              Volta só a metade que se verifica.
            */}
            Condições vigentes avaliadas sobre os dados reais do mandato. Cada alerta traz a regra
            que o gerou e as entidades de origem. Não há alerta arquivado:
            quando a condição deixa de valer, o alerta desaparece sozinho. Marcar como lido não
            encerra a condição, só tira o alerta da contagem de pendentes.
          </p>
        </div>
        <div className="act">
          <button className="btn" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Reavaliando...' : 'Reavaliar regras'}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Alertas ativos</div>
          <div className="v">{alertas.length}</div>
          <div className="d">{naoLidos} ainda não lido(s)</div>
        </div>
        <div className="card kpi">
          <div className="l">Críticos</div>
          <div className="v" style={{ color: contar('critica') ? 'var(--crit)' : 'var(--good)' }}>
            {contar('critica')}
          </div>
          <div className="d">exigem decisão imediata</div>
        </div>
        <div className="card kpi">
          <div className="l">Pontos de atenção</div>
          <div className="v" style={{ color: 'var(--warn)' }}>{contar('atencao')}</div>
          <div className="d">acompanhar de perto</div>
        </div>
        <div className="card kpi">
          <div className="l">Oportunidades</div>
          <div className="v" style={{ color: 'var(--good)' }}>{contar('oportunidade')}</div>
          <div className="d">espaço de crescimento identificado</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters">
        <button
          type="button"
          className={`chip neutral chipbtn ${filtro === '' ? 'on' : ''}`}
          onClick={() => setFiltro('')}
        >
          Todos ({alertas.length})
        </button>
        <button
          type="button"
          className={`chip neutral chipbtn ${filtro === 'nao-lidos' ? 'on' : ''}`}
          onClick={() => setFiltro('nao-lidos')}
        >
          Não lidos ({naoLidos})
        </button>
        {(['critica', 'atencao', 'oportunidade'] as SeveridadeAlerta[]).map((s) => (
          <button
            key={s}
            type="button"
            className={`chip neutral chipbtn ${filtro === s ? 'on' : ''}`}
            onClick={() => setFiltro(s)}
          >
            {ROTULO[s]} ({contar(s)})
          </button>
        ))}
      </div>

      {marcar.isError && (
        <div className="note warn" role="alert" style={{ marginTop: 10, fontSize: 12 }}>
          Não foi possível marcar o alerta como lido: a marcação não foi gravada. Tente de novo.
        </div>
      )}

      <div className="note" style={{ marginTop: 10, fontSize: 12 }}>
        Marcar como lido vale para o mandato inteiro, não para cada usuário: quem marcar tira o
        alerta da contagem de todos.{' '}
        <Info titulo="Como os alertas são avaliados">
          As regras cruzam território, lideranças, ações, solicitações e contatos. Cada alerta
          mostra a condição que o disparou e as entidades consultadas.
        </Info>
      </div>

      {/* Lista */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {lista.map((a) => (
          <div
            key={a.id}
            className="card"
            style={{
              borderLeft: `4px solid var(--${
                a.severidade === 'critica' ? 'crit' : a.severidade === 'atencao' ? 'warn' : 'good'
              })`,
              // Lido continua na lista, apenas esmaecido (proto:1008): a
              // condição segue vigente, e esconder o cartão faria a tela
              // sugerir que o problema acabou.
              opacity: a.lida ? 0.6 : 1,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <span className={`chip ${CHIP[a.severidade]}`}>{ROTULO[a.severidade]}</span>
                {a.lida && (
                  <span className="chip neutral" style={{ marginLeft: 6 }}>
                    Lido
                  </span>
                )}
                <b style={{ display: 'block', fontSize: 14, marginTop: 6 }}>{a.titulo}</b>

                <dl className="dl" style={{ marginTop: 8, fontSize: 12 }}>
                  <dt>Regra</dt>
                  <dd className="mono">{a.regra}</dd>
                  <dt>Fonte</dt>
                  <dd className="mono">{a.fonte}</dd>
                  {/*
                    Período REAL da regra, não o do protótipo. A maioria das
                    regras avalia o estado de agora e não tem recorte
                    temporal; o texto diz isso em vez de anunciar uma janela
                    que a consulta não aplica.
                  */}
                  <dt>Período</dt>
                  <dd>{a.periodo}</dd>
                </dl>

                {a.lacuna && (
                  <div className="note warn" style={{ marginTop: 8, fontSize: 12 }}>
                    {a.lacuna}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 6, flexDirection: 'column', flex: 'none' }}>
                <Link
                  to={a.link}
                  className="btn sm pri"
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={() => abrirRegistros(a)}
                >
                  Abrir registros
                </Link>

                {/*
                  Só existe quando há destino que de fato cria o que o alerta
                  pede. O protótipo mostra "Ação sugerida" em todos e, em três
                  dos cinco, apenas emite `toast('Ação sugerida criada')` sem
                  criar nada — o rótulo aqui é "Criar ação" porque é o que o
                  clique faz: abre o formulário de nova ação.
                */}
                {a.acao && (
                  <Link to={a.acao.link} className="btn sm" style={{ whiteSpace: 'nowrap' }}>
                    {a.acao.rotulo}
                  </Link>
                )}

                <button
                  className="btn sm"
                  style={{ whiteSpace: 'nowrap' }}
                  disabled={marcar.isPending}
                  onClick={() => marcar.mutate({ id: a.id, lida: !a.lida })}
                >
                  {a.lida ? 'Marcar como não lido' : 'Lido'}
                </button>
              </div>
            </div>
          </div>
        ))}

        {isLoading && <div className="empty">Avaliando regras...</div>}
        {!isLoading && lista.length === 0 && (
          <div className="empty">
            {alertas.length === 0
              ? 'Nenhuma condição de alerta ativa no momento.'
              : filtro === 'nao-lidos'
                ? 'Todos os alertas ativos já foram lidos.'
                : 'Nenhum alerta nesta severidade.'}
          </div>
        )}
      </div>

    </div>
  );
}
